import Box from '@app/components/Box';
import Flex from '@app/components/Flex';
import InputGroupSpinnerElement from '@app/components/InputGroupSpinnerElement';
import { FormGroup, Icon, InputGroup, Spinner } from '@blueprintjs/core';
import { Endpoints } from '@data/consts';
import { fetchTask } from '@pages/Tasks/store/effects';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlatformTaskFilterQueryMode } from './enums';

export interface PlatformTaskFilterQueryInputProps {
    loading: boolean;
    value: string;
    placeholder: string;
    shouldBeSearched: boolean;
    onChange: (e) => void;
    onSearch: (newSearch: string) => void;
}

export type Props = PlatformTaskFilterQueryInputProps & HTMLAttributes<HTMLDivElement>;

const PlatformTaskFilterQueryInput: React.FC<Props> = ({
    loading,
    value,
    shouldBeSearched,
    placeholder,
    onSearch,
    onChange,
    ...props
}) => {
    const navigate = useNavigate();

    const [mode, setMode] = useState(PlatformTaskFilterQueryMode.NONE);
    const [hint, setHint] = useState('');

    const handleAssignHint = () => {
        switch (mode) {
            case PlatformTaskFilterQueryMode.NONE:
                setHint(`Hint: type ‘#RequestId’ and press ‘enter’ to go directly to the request`);
                break;
            case PlatformTaskFilterQueryMode.JUMP_TO_TASK:
                const trimmedValue = value.trim();
                const taskId = trimmedValue.slice(1);
                setHint(`Hint: press 'enter' to jump to request #${taskId}`);
                break;
        }
    };

    const handleSetModeExecution = () => {
        switch (mode) {
            case PlatformTaskFilterQueryMode.JUMP_TO_TASK:
                const trimmedValue = value.trim();
                const taskId = trimmedValue.slice(1);
                fetchTask(taskId)
                    .then(() => {
                        navigate(Endpoints.TASK_INFORMATION.replace(':taskId', taskId));
                    })
                    .catch((e) => {
                        // TODO: handle error
                        console.error(e);
                    });
                break;
        }
    };

    const handleKeyPressed = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSetModeExecution();
        }
    };

    useEffect(() => {
        if (value.trim().length !== 0) {
            // Task ID - #NUMBER
            if (/^#\d+$/.test(value.trim())) {
                setMode(PlatformTaskFilterQueryMode.JUMP_TO_TASK);
            } else {
                setMode(PlatformTaskFilterQueryMode.NONE);
            }
        } else {
            setMode(PlatformTaskFilterQueryMode.NONE);
            setHint(`Hint: type ‘#RequestId’ and press ‘enter’ to go directly to the request`);
        }
    }, [mode, value]);

    useEffect(() => {
        if (shouldBeSearched && mode === PlatformTaskFilterQueryMode.NONE) {
            const timeoutId = setTimeout(() => {
                onSearch(value);
            }, 400);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        handleAssignHint();

        // eslint-disable-next-line
    }, [mode, value]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPressed);

        return () => {
            document.removeEventListener('keydown', handleKeyPressed);
        };
    });

    const renderQueryInputElement = () => {
        if (loading) {
            return (
                <Box width="40px" height="41px">
                    <Flex fullWidth fullHeight align="center" justify="center">
                        <Spinner size={16} />
                    </Flex>
                </Box>
            );
        } else {
            return <Icon icon="search" size={16} />;
        }
    };

    return (
        <div {...props}>
            <FormGroup helperText={hint}>
                <InputGroup
                    large
                    leftIcon="search"
                    placeholder={placeholder}
                    value={value}
                    leftElement={<InputGroupSpinnerElement loading={loading} />}
                    onChange={onChange}
                />
            </FormGroup>
        </div>
    );
};

export default PlatformTaskFilterQueryInput;
